// @types
// components
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
// @mui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IOrder, IReturnableFulfillments } from 'src/@types/order';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { createReturn } from 'src/redux/slices/order';
import * as Yup from 'yup';
import Image from '../../../components/image';
import { CustomProduct } from './OrderNewEditForm';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  products: CustomProduct[];
  order: IOrder;
  returnableFulfillments: IReturnableFulfillments;
  onClose: VoidFunction;
  onReturn: VoidFunction;
};

export default function ReturnDialog({
  onClose,
  open,
  products,
  order,
  returnableFulfillments,
  onReturn,
}: Props) {
  const { translate } = useLocales();

  const getMaxQuantity = (product: CustomProduct) => {
    const a = returnableFulfillments
      .find(
        (e) =>
          Number(e.fulfillmentId) ===
          Number(product?.fulfillmentLine?.fulfillment_id)
      )
      ?.returnableFulfillmentLineItems.find(
        (rf) => rf.lineItemId === product.external_id
      );

    return a?.quantity || 0;
  };

  const [localProducts, setLocalProducts] = useState<
    (CustomProduct & { maxQuantity: number })[]
  >((products || []).map((p) => ({ ...p, maxQuantity: getMaxQuantity(p) })));

  const schema = Yup.object().shape({
    trackingNumber: Yup.string(),
    trackingUrl: Yup.string(),
  });

  useEffect(() => {
    setLocalProducts(
      (products || []).map((p) => ({ ...p, maxQuantity: getMaxQuantity(p) }))
    );
  }, [products, setLocalProducts]);

  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const changeProductQuantity = (product: CustomProduct, quantity: number) => {
    const newProducts = localProducts.flatMap((p) => {
      if (p.external_id === product.external_id) {
        return {
          ...p,
          quantity:
            quantity === 0
              ? undefined
              : quantity > p.maxQuantity
                ? p.maxQuantity
                : quantity,
        };
      }

      return p;
    });

    setLocalProducts(newProducts);
  };

  const onSubmit = handleSubmit(async (_data) => {
    const fulfillmentLine = localProducts[0].fulfillmentLine;

    console.log('fulfillment', fulfillmentLine, localProducts);
    if (!fulfillmentLine) return;

    console.log('ddd', returnableFulfillments);

    const returnableFulfillment = returnableFulfillments.find(
      (e) => e.fulfillmentId === fulfillmentLine.fulfillment_id
    );

    console.log('returnableFulfillment', returnableFulfillment);
    console.log(fulfillmentLine, localProducts);

    await createReturn(
      order.external_id,
      fulfillmentLine.fulfillment_id,
      localProducts
        .filter((l) => (l.quantity || 0) > 0)
        .map((p) => {
          const lineItem = fulfillmentLine.lines.find(
            (i) =>
              String(i.lineItemId) === String(p.external_id) ||
              String(i.lineItemId) === String(p.external_id)
          );

          return {
            id:
              returnableFulfillment?.returnableFulfillmentLineItems?.find(
                (rf) => rf.lineItemId === p.external_id
              )?.fulfillmentLineItemId || '',
            lineItemId: String(lineItem?.lineItemId),
            quantity: p.quantity || 0,
          };
        })
    );

    onReturn();

    onClose();
  });

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{`${translate('fulfillmentDialog.title')}`}</DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item xs={12} md={12}>
              {localProducts.map((product, index) => (
                <>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems="center"
                    spacing={2}
                    sx={{ width: 1 }}
                  >
                    <Grid container spacing={2}>
                      <Grid item md={1}>
                        {product?.images?.[0]?.src || product?.image?.src ? (
                          <Image
                            disabledEffect
                            visibleByDefault
                            alt={product.title}
                            src={
                              product?.images?.[0]?.src || product?.image?.src
                            }
                            sx={{ borderRadius: 1.5, width: 48, height: 48 }}
                          />
                        ) : (
                          <Iconify
                            icon="eva:file-remove-outline"
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1.5,
                              mr: '2em',
                            }}
                          />
                        )}
                      </Grid>
                      <Grid
                        item
                        md={8}
                        sx={{
                          ml: '1em',
                          mt: product.variants[0]?.sku ? '-.5em' : '.5em',
                        }}
                      >
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            my: 0.5,
                            color: 'info.main',
                            fontWeight: 'fontWeightMedium',
                          }}
                        >
                          {product.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            my: 0.5,
                            color: 'info.main',
                            fontWeight: 'fontWeightMedium',
                          }}
                        >
                          {product.variants?.[0]?.sku
                            ? `SKU: ${product.variants[0].sku} - `
                            : ''}
                          {product.variants?.[0]?.barcode
                            ? `Barcode: ${product.variants[0].barcode}`
                            : ''}
                        </Typography>
                      </Grid>
                      <Grid item md={2}>
                        <RHFTextField
                          size="small"
                          name={`products[${index}].quantity`}
                          label={`${translate('quantity')}`}
                          value={product.quantity}
                          placeholder="0"
                          onChange={(event) =>
                            changeProductQuantity(
                              product,
                              Number(event.target.value)
                            )
                          }
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            type: 'number',
                            endAdornment: (
                              <InputAdornment position="start">
                                /{product.maxQuantity}
                              </InputAdornment>
                            ),
                          }}
                          sx={{ maxWidth: { md: 96 } }}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {`${translate('cancel')}`}
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            {`${translate('return')}`}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
